/**
 * Header data for the A4 ticket check-in sheet, plus the mapping from a live
 * WorkItem and the built-in sample fixtures used for template previews.
 *
 * Only the header is data-driven — the inspection tables, notes, collection and
 * pricing fields are printed blank for the technician to hand-fill.
 */
import type {
  CheckInDeviceType,
  CheckInDraftJob,
  CheckInYesNo,
  WorkItem,
} from '@techgitter/contracts';

import { INSPECTION_SCHEMA, inspectionToDraft } from '../../../utils/inspectionSchema';
import { checkInDeviceLabel } from '@techgitter/contracts';

export type TicketSheetVariant = 'retail' | 'monthly';

export interface TicketSheetData {
  /** RepairShopr ticket number, e.g. "8261205" (blank if not yet numbered). */
  ticketNumber: string;
  /** Ticket subject / issue line. */
  subject: string;
  /** Drop-off date/time, pre-formatted in Australia/Sydney. */
  dateTime: string;
  /** Customer line — "Business - Contact" when a business name is present. */
  contactName: string;
  /** Best phone number (mobile preferred, then phone). */
  phone: string;
  /** Customer email. */
  email: string;
  /**
   * Pre-printed prefix on the invoice-ID fields — the first 4 digits of the
   * current invoice-number sequence (e.g. "2611"), so the technician only writes
   * the last two. Omitted here (derived asynchronously by the print page); the
   * sheet falls back to `INVOICE_ID_PREFIX` when absent.
   */
  invoiceIdPrefix?: string;
}

/**
 * Format an ISO timestamp as e.g. "06 July 2026" (dd mmmm yyyy) in
 * Australia/Sydney. Built via formatToParts so the layout is deterministic
 * across environments (per the global locale rule — never emit bare UTC).
 */
export function formatSheetDateTime(iso: string | undefined | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${get('month')} ${get('year')}`;
}

/** Build the "Business - Contact" (or plain contact) header line. */
function buildContactName(customerName: string, businessName?: string): string {
  const contact = (customerName ?? '').trim();
  const business = (businessName ?? '').trim();
  if (business && contact && business !== contact) return `${business} - ${contact}`;
  return business || contact;
}

/** Map a live WorkItem to the sheet header fields. */
export function mapWorkItemToTicketSheetData(workItem: WorkItem): TicketSheetData {
  return {
    ticketNumber: workItem.number != null ? String(workItem.number) : '',
    subject: workItem.title ?? '',
    dateTime: formatSheetDateTime(workItem.createdAt || workItem.createdOn),
    contactName: buildContactName(workItem.customerName, workItem.customerBusinessName),
    phone: workItem.customerMobile || workItem.customerPhone || '',
    email: workItem.customerEmail || '',
  };
}

/**
 * Built-in sample fixtures, modelled on the two scanned example sheets, so the
 * template can be previewed/printed without a live ticket ("use live examples"
 * toggle → off). retail = residential (pricing boxes); monthly = business
 * (billed on monthly invoice).
 */
const RETAIL_SAMPLE: TicketSheetData = {
  ticketNumber: '8261205',
  subject: 'PC - Techsage and Webroot - curser sometimes erratic too',
  dateTime: '06 July 2026',
  contactName: 'Robert Hagon',
  phone: '61 4252 04175',
  email: 'roberthagon@bigpond.com',
};

const MONTHLY_SAMPLE: TicketSheetData = {
  ticketNumber: '8260945',
  subject: 'New laptop setup',
  dateTime: '26 May 2026',
  contactName: 'RegenCo Natural Capital Pty Ltd - Mitchell Board',
  phone: '458 972 096',
  email: 'mitchell.board@regenco.earth',
};

export function buildSampleTicketSheetData(variant: TicketSheetVariant): TicketSheetData {
  return variant === 'monthly' ? { ...MONTHLY_SAMPLE } : { ...RETAIL_SAMPLE };
}

// ---------------------------------------------------------------------------
// Captured check-in inspection → sheet display model
//
// When the sheet is printed for a check-in session whose device inspection is
// complete, the Reception check-in inspection region prints the captured
// values (make/serial + each Yes/No check + notes) instead of blank hand-fill
// rows. One captured block maps to one printed sheet (one device per page).
// ---------------------------------------------------------------------------


/** One filled inspection row — the schema label plus the captured Yes/No + notes. */
export interface CapturedCheckRow {
  key: string;
  label: string;
  status: CheckInYesNo | null;
  notes: string | null;
}

/** All details captured for one device, shaped for the printed sheet. */
export interface CapturedInspection {
  deviceType: CheckInDeviceType;
  deviceLabel: string;
  manufacturer: string;
  serial: string;
  brandNewInBox: boolean;
  damageRows: CapturedCheckRow[];
  itemStatusRows: CapturedCheckRow[];
  otherNotes: string | null;
  serviceSummary: string;
  photoCount: number;
  /**
   * Human-facing RS ticket number for this device (e.g. "8261300"), when the
   * orchestrator captured it. Deliberately NOT falling back to the RS ticket
   * id: this value is printed under "TICKET NO." on the sheet, and the opaque
   * internal id there reads as a wrong ticket number. Empty when unknown so
   * the print page can fall back to the work item's own number.
   */
  ticketNumber: string;
  completedAt: string | null;
  completedBy: string | null;
}

/** Format an ISO timestamp as "06 July 2026, 3:14 pm" in Australia/Sydney. */
export function formatSheetTimestamp(iso: string | undefined | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Map a completed check-in draft job to the captured-inspection display model.
 * Uses INSPECTION_SCHEMA for the device-specific row set and labels so a phone
 * / hard-drive / etc. prints its own rows rather than the laptop defaults.
 */
export function mapDraftJobToCapturedInspection(job: CheckInDraftJob): CapturedInspection {
  const schema = INSPECTION_SCHEMA[job.deviceType];
  const draft = inspectionToDraft(job.deviceType, job.inspection);

  const toRows = (
    rows: readonly { key: string; label: string }[],
    checks: Record<string, { status: CheckInYesNo | null; notes: string | null }>,
  ): CapturedCheckRow[] =>
    rows.map(row => ({
      key: row.key,
      label: row.label,
      status: checks[row.key]?.status ?? null,
      notes: checks[row.key]?.notes ?? null,
    }));

  return {
    deviceType: job.deviceType,
    deviceLabel: checkInDeviceLabel(job),
    manufacturer: draft.manufacturer || '—',
    serial: draft.serialNumber || (draft.serialNotLegible ? 'Not legible' : '—'),
    brandNewInBox: draft.brandNewInBox,
    damageRows: toRows(schema.damageRows, draft.damageChecks),
    itemStatusRows: toRows(schema.itemStatusRows, draft.itemStatusChecks),
    otherNotes: draft.otherNotes,
    serviceSummary: job.serviceSummary,
    photoCount: job.photos?.length ?? 0,
    ticketNumber: job.repairShoprTicketNumber ?? '',
    completedAt: job.inspectionCompletedAt,
    completedBy: job.inspectionCompletedBy,
  };
}

/** A completed inspection is one that reached 'complete' with an inspection body. */
export function isInspectionComplete(job: CheckInDraftJob): boolean {
  return job.inspectionStatus === 'complete' && job.inspection != null;
}
